/**
 * What a Kanban card's meta row shows (Boards Redesign cards 2.1 + 2.4).
 *
 * Pure, so the rules are unit-tested without rendering a board:
 *
 *  - the meta row (priority · due · comments · description · assignee)
 *    renders only when it has something to say. A bare task is the one-row
 *    "Minimal" card of the design (42 px), not a title over an empty band.
 *  - "Medium" is the priority every new task is created with, so on its own
 *    it is not something to say: a bare medium task stays minimal and its
 *    priority is set from the card's ⋯ menu. Low or High, or any other meta,
 *    brings the row (and the clickable signal with it) back.
 *  - a closed card (done / dismissed) shows its priority like an open one
 *    (owner, 13.10.2026 — it used to hide it; the list view follows).
 *  - on a team board the assign placeholder is always there, so the row is
 *    too (an earlier owner decision this keeps) — but a row that holds only
 *    that placeholder shows no Medium signal beside it.
 *  - the due date is a short chip ("18 Oct"); overdue says by how much
 *    ("9 Oct · 2 d late"). "Overdue" itself comes from boardFilters.isOverdue
 *    so the chip and the Overdue filter cannot disagree.
 *  - subtasks show as "done/total" after the due chip; a task with subtasks
 *    always has a meta row, and a done card keeps its "3/3".
 *  - a closed card says when it closed ("done 10 Oct", from `doneAt`);
 *    tasks closed before the date was recorded show nothing.
 */
import { isOverdue } from './boardFilters';
import { subtaskCounts } from './subtasks';
import { doneAtOf, taskStatusOf } from './taskStatus';

const PRIORITIES = ['low', 'medium', 'high'];
const DAY_MS = 24 * 60 * 60 * 1000;

/** A stored due date as a LOCAL calendar day. `YYYY-MM-DD` is what the date
 *  picker writes; `new Date('2026-10-09')` would be UTC midnight, which is the
 *  previous evening west of Greenwich and prints the wrong day. */
export function parseDueDay(dueDate) {
  if (!dueDate) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dueDate));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dueDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * The due chip: `{ text, overdue, daysLate, full }` or null.
 * `text` is the short form ("18 Oct", with the year when it is not this
 * year's); `full` the long date for the tooltip / aria-label.
 */
export function formatDueChip(dueDate, { now = new Date(), locale } = {}) {
  const day = parseDueDay(dueDate);
  if (!day) return null;
  const sameYear = day.getFullYear() === now.getFullYear();
  const short = day.toLocaleDateString(locale, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  const full = day.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const overdue = isOverdue(dueDate, now);
  if (!overdue) return { text: short, overdue: false, daysLate: 0, full };
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  const daysLate = Math.max(1, Math.round((today - day) / DAY_MS));
  return { text: `${short} · ${daysLate} d late`, overdue: true, daysLate, full };
}

/** "done 10 Oct" for a closed task with a done date (the year when it is not
 *  this year's), else null. `full` is the long date for the tooltip. */
export function formatDoneDate(task, { now = new Date(), locale } = {}) {
  const at = doneAtOf(task);
  if (!at) return null;
  const sameYear = at.getFullYear() === now.getFullYear();
  const short = at.toLocaleDateString(locale, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  const full = at.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const word = taskStatusOf(task) === 'dismissed' ? 'dismissed' : 'done';
  return { text: `${word} ${short}`, full, word };
}

/** A description counts when it has TEXT — an emptied rich-text editor leaves
 *  `<p></p>` behind, which is not a description. */
export function hasDescription(task) {
  const raw = task?.descriptionHtml ?? task?.description ?? '';
  return !!String(raw).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/**
 * Everything the card needs to decide its meta row.
 * `assignable` = the board can assign from the card (a team board).
 */
export function cardMetaOf(task, { assignable = false, now = new Date(), locale } = {}) {
  const closed = !!task?.done;
  const priority = PRIORITIES.includes(task?.priority) ? task.priority : 'medium';
  const due = formatDueChip(task?.dueDate, { now, locale });
  const commentCount = Array.isArray(task?.comments) ? task.comments.length : 0;
  const described = hasDescription(task);
  const assigned = task?.assigneeId != null && task.assigneeId !== '';
  const showAssignee = assigned || !!assignable;
  const subtasks = subtaskCounts(task);
  const doneDate = closed ? formatDoneDate(task, { now, locale }) : null;
  // Something on the row besides an empty "assign" slot. Without it the row
  // is only the placeholder, and a default-Medium signal next to it would be
  // the bars of a card that has nothing to say (Card anatomy "Minimal").
  const hasContent = !!due || subtasks.total > 0 || !!doneDate || commentCount > 0 || described || assigned || priority !== 'medium';
  const hasMeta = hasContent || showAssignee;
  return {
    hasMeta,
    priority,
    showPriority: hasContent,
    // Only the empty "assign" slot: the card draws it at the end of the
    // title row instead of a row of its own, so a bare team-board card stays
    // the one-row Minimal card (42 px) and the slot is still always visible.
    placeholderOnly: showAssignee && !hasContent,
    due,
    subtasks,
    doneDate,
    commentCount,
    hasDescription: described,
    // Unassigned on a team board the "assign" placeholder is ALWAYS shown
    // (TaskAssignee.css: hiding it made assigning discoverable only by
    // chance), so on a team board every card has a meta row; the one-row
    // minimal card is a personal-board card.
    showAssignee,
  };
}
